import { useMemo, useState } from "react";
import { collapseDiff, lineDiff, type DiffLine } from "../../utils/lineDiff";
import classes from "./PromptDiff.module.css";

interface PromptDiffProps {
  before: string;
  after: string;
  /**
   * Linhas iguais mantidas em volta de cada mudança; o resto recolhe num
   * trecho que expande ao clicar. Sem ele, o prompt inteiro aparece.
   */
  context?: number;
}

const MARKER = { add: "+ ", del: "− ", same: "  " } as const;

/** Quantas linhas mudam entre dois prompts (para o rótulo "N linha(s)"). */
export const countChangedLines = (before: string, after: string) =>
  lineDiff(before, after).filter((line) => line.kind !== "same").length;

const Line = ({ line }: { line: DiffLine }) => (
  <span
    className={classes.line}
    data-kind={line.kind}>
    {MARKER[line.kind]}
    {line.text}
    {"\n"}
  </span>
);

/**
 * Diff por linha (+ verde, − vermelho riscado). Quem chama decide o container
 * rolável: o drawer do histórico ocupa a altura toda, o modal da análise cabe
 * numa área fixa.
 */
export function PromptDiff({ before, after, context }: PromptDiffProps) {
  const diff = useMemo(() => lineDiff(before, after), [before, after]);
  const segments = useMemo(
    () => (context === undefined ? diff : collapseDiff(diff, context)),
    [diff, context]
  );
  // Trechos recolhidos que a pessoa abriu, pela posição.
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());

  return (
    <pre className={classes.pre}>
      {segments.map((segment, index) => {
        if (segment.kind !== "gap") {
          return (
            <Line
              key={`${index}-${segment.kind}`}
              line={segment}
            />
          );
        }
        if (expanded.has(index)) {
          return segment.lines.map((line, offset) => (
            <Line
              key={`${index}-${offset}`}
              line={line}
            />
          ));
        }
        return (
          <button
            key={`${index}-gap`}
            type="button"
            className={classes.gap}
            onClick={() => setExpanded((open) => new Set(open).add(index))}>
            {`Mostrar ${segment.lines.length} linhas iguais`}
          </button>
        );
      })}
    </pre>
  );
}
